import { formatMoney, formatQuantity } from "./money";
import type {
  BillingCadence,
  Quotation,
  QuotationLineItem,
  QuotationTotals,
} from "./model";
import { calculateLineAmountMinor } from "./totals";

interface QuotationPreviewProps {
  quotation: Quotation;
  totals: QuotationTotals;
}

function display(value: string, fallback: string): string {
  return value.trim().length > 0 ? value : fallback;
}

function PreviewItems({
  quotation,
  cadence,
}: {
  quotation: Quotation;
  cadence: BillingCadence;
}) {
  const items = quotation.lineItems.filter(
    (lineItem) => lineItem.billingCadence === cadence,
  );
  if (items.length === 0) {
    return null;
  }

  const amountLabel = (lineItem: QuotationLineItem): string => {
    if (lineItem.optional && !lineItem.included) {
      return "Excluded";
    }

    const amount = calculateLineAmountMinor(lineItem);
    return amount === null
      ? "TBD"
      : formatMoney(amount, quotation.currency, quotation.currencyMinorUnits);
  };

  return (
    <section className="quotation-paper__items">
      <h4>{cadence === "recurring" ? "Recurring charges" : "One-time charges"}</h4>
      <div className="quotation-paper__table" role="table">
        <div className="quotation-paper__table-head" role="row">
          <span role="columnheader">Item</span>
          <span role="columnheader">Qty</span>
          <span role="columnheader">Amount</span>
        </div>
        {items.map((lineItem) => (
          <div
            className={`quotation-paper__table-row${
              lineItem.optional && !lineItem.included
                ? " quotation-paper__table-row--excluded"
                : ""
            }`}
            role="row"
            key={lineItem.id}
          >
            <span role="cell">
              <strong>{display(lineItem.description, "Untitled item")}</strong>
              {lineItem.details.trim().length > 0 ? (
                <small>{lineItem.details}</small>
              ) : null}
              <small>{lineItem.provenance.label}</small>
            </span>
            <span role="cell">
              {formatQuantity(lineItem.quantityMilli)} {lineItem.unit}
            </span>
            <span role="cell">{amountLabel(lineItem)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function QuotationPreview({ quotation, totals }: QuotationPreviewProps) {
  return (
    <div className="quotation-preview-shell">
      <div className="quotation-preview-shell__toolbar">
        <div>
          <strong>Document preview</strong>
          <span>US Letter - fit width</span>
        </div>
        <span>Page 1</span>
      </div>

      <article className="quotation-paper" aria-label="Quotation preview">
        <header className="quotation-paper__header">
          <div>
            <h2>{display(quotation.seller.name, "Your business")}</h2>
            <p>{quotation.seller.address}</p>
            <p>
              {[quotation.seller.email, quotation.seller.phone]
                .filter((value) => value.trim().length > 0)
                .join(" - ")}
            </p>
          </div>
          <div className="quotation-paper__title">
            <span>Quotation</span>
            <dl>
              <div>
                <dt>Number</dt>
                <dd>{display(quotation.quotationNumber, "Draft")}</dd>
              </div>
              <div>
                <dt>Issued</dt>
                <dd>{quotation.issueDate}</dd>
              </div>
              <div>
                <dt>Valid until</dt>
                <dd>{quotation.validUntil}</dd>
              </div>
            </dl>
          </div>
        </header>

        <section className="quotation-paper__customer">
          <span>Prepared for</span>
          <h3>{display(quotation.customer.name, "Customer")}</h3>
          <p>{quotation.customer.address}</p>
          <p>
            {[quotation.customer.email, quotation.customer.phone]
              .filter((value) => value.trim().length > 0)
              .join(" - ")}
          </p>
        </section>

        <section className="quotation-paper__intro">
          <h3>{display(quotation.title, "Quotation")}</h3>
          {quotation.introduction.trim().length > 0 ? (
            <p>{quotation.introduction}</p>
          ) : null}
        </section>

        <PreviewItems quotation={quotation} cadence="recurring" />
        <PreviewItems quotation={quotation} cadence="one-time" />

        {quotation.lineItems.length === 0 ? (
          <p className="quotation-paper__empty">
            Add a recurring or one-time item to build this quotation.
          </p>
        ) : null}

        <section className="quotation-paper__totals">
          {totals.recurring.includedLineCount > 0 ? (
            <div>
              <span>Recurring subtotal</span>
              <strong>
                {totals.recurring.authoritativeSubtotalMinor === null
                  ? "TBD"
                  : formatMoney(
                      totals.recurring.authoritativeSubtotalMinor,
                      quotation.currency,
                      quotation.currencyMinorUnits,
                    )}
              </strong>
            </div>
          ) : null}
          {totals.oneTime.includedLineCount > 0 ? (
            <div>
              <span>One-time subtotal</span>
              <strong>
                {totals.oneTime.authoritativeSubtotalMinor === null
                  ? "TBD"
                  : formatMoney(
                      totals.oneTime.authoritativeSubtotalMinor,
                      quotation.currency,
                      quotation.currencyMinorUnits,
                    )}
              </strong>
            </div>
          ) : null}
          <div className="quotation-paper__grand-total">
            <span>Grand total</span>
            <strong>
              {totals.grandTotalMinor === null
                ? "TBD"
                : formatMoney(
                    totals.grandTotalMinor,
                    quotation.currency,
                    quotation.currencyMinorUnits,
                  )}
            </strong>
          </div>
          {totals.hasUnknownIncludedValues ? (
            <p>An included value is still TBD; no authoritative total is shown.</p>
          ) : null}
        </section>

        {quotation.terms.some((term) => term.trim().length > 0) ? (
          <section className="quotation-paper__terms">
            <h4>Terms</h4>
            <ul>
              {quotation.terms
                .filter((term) => term.trim().length > 0)
                .map((term, index) => (
                  <li key={`${index}-${term}`}>{term}</li>
                ))}
            </ul>
          </section>
        ) : null}

        {quotation.preparedBy.trim().length > 0 ? (
          <p className="quotation-paper__prepared">
            Prepared by <strong>{quotation.preparedBy}</strong>
          </p>
        ) : null}
        {quotation.closingNote.trim().length > 0 ? (
          <p className="quotation-paper__closing">{quotation.closingNote}</p>
        ) : null}
      </article>
    </div>
  );
}
